// Change-email modal: HTTP 412 and HTTP 422 errors render inline under the email input.
// Finished HTTP 200 may call changeBrazeUserIfEnabled when the gated id is new. Pending does not.
import React from 'react';
import PropTypes from 'prop-types';
import Loading from '../../../shared/loading';
import Validator from '../../../shared/validator';
import mutate from '../../../shared/mutate';
import parseFailXhr from '../../../shared/parse-fail-xhr';
import { EMAIL_REGEX } from '../../../shared/consts';
import { setEmail } from '../../../services/profiles/personal-details-service';
import { changeBrazeUserIfEnabled } from '../../../services/braze/braze-user-identity';

function failStatus(error) {
  if (error.response && error.response.status) return error.response.status;
  if (error.jqXHR && error.jqXHR.status) return error.jqXHR.status;
  return null;
}

function failMessage(error) {
  const data = (error.response && error.response.data)
    || (error.jqXHR && error.jqXHR.responseJSON);
  return data && typeof data.message === 'string' ? data.message : null;
}

function currentBrazeSdkUserId() {
  const sdk = window.braze;
  if (!sdk || typeof sdk.getUser !== 'function') return null;
  const brazeUser = sdk.getUser();
  if (!brazeUser || typeof brazeUser.getUserId !== 'function') return null;
  const userId = brazeUser.getUserId();
  return typeof userId === 'string' && userId ? userId : null;
}

export function unprocessableMessage(error) {
  if (failStatus(error) !== 422) return null;
  return failMessage(error);
}

export function switchBrazeIfNeeded(data) {
  if (!data || data.pending) return;
  const gatedId = data.braze_external_id;
  if (!gatedId || typeof gatedId !== 'string') return;
  const sdkUserId = currentBrazeSdkUserId();
  if (sdkUserId) {
    if (sdkUserId !== gatedId) changeBrazeUserIfEnabled(gatedId);
    return;
  }
  if (data.braze_external_id_changed) changeBrazeUserIfEnabled(gatedId);
}

export default class EmailChangeModal extends React.Component {
  static propTypes = {
    onEmailSaved: PropTypes.func,
  }

  static defaultProps = {
    onEmailSaved: null,
  }

  constructor(props) {
    super(props);

    this.validator = new Validator({
      emailRequired: state => !!state.email,
      emailFormat: state => !state.email || EMAIL_REGEX.test(state.email),
      emailIsUnique: state => state.emailIsUnique,
    });
    this.state = {
      email: '',
      emailIsUnique: true,
      serverError: null,
      validations: this.validator.getValidations(),
    };
  }

  componentDidMount() {
    const self = this;

    this.$modal.click(function onClick(e) {
      if (e.target === this) self.dismiss();
    });
  }

  handleEmailChange(e) {
    const email = e.target.value;

    this.setState(oldState => mutate(oldState, state => {
      state.email = email;
      state.emailIsUnique = true;
      state.serverError = null;
      state.validations.emailRequiredValid = this.validator.validateEmailRequired(state);
      state.validations.emailFormatValid = this.validator.validateEmailFormat(state);
      state.validations.emailIsUniqueValid = this.validator.validateEmailIsUnique(state);
    }));
  }

  save(evt) {
    evt && evt.preventDefault && evt.preventDefault();

    this.setState(state => ({
      serverError: null,
      validations: this.validator.validateForm(state),
    }), () => {
      if (!this.validator.formValid) return;

      this.loading.start();
      setEmail(this.state.email)
        .then(response => {
          this.loading.stop();
          this.dismiss();
          this.props.onEmailSaved && this.props.onEmailSaved(this.state.email);
          switchBrazeIfNeeded(response && response.data);
        })
        .catch(e => {
          this.loading.stop();

          const message = unprocessableMessage(e);
          if (message) {
            this.setState({ serverError: message });
            return;
          }

          const xhrLike = e.jqXHR || (e.response && { responseJSON: e.response.data }) || {};
          const errors = parseFailXhr(xhrLike);

          if (errors.email && errors.email.hasAlreadyBeenTaken) {
            this.setState(oldState => mutate(oldState, state => {
              state.emailIsUnique = false;
              state.validations.emailIsUniqueValid = this.validator.validateEmailIsUnique(state);
            }));

            errors.email.hasAlreadyBeenTaken.handled = true;
          }

          if (errors.anyUnhandledErrors()) {
            this.setState({ serverError: errors.getErrorSummary() });
          }
        });
    });
  }

  present(e, currentEmailAddress) {
    e && e.preventDefault && e.preventDefault();

    this.validator.resetValidation();
    this.setState({
      email: currentEmailAddress,
      serverError: null,
      validations: this.validator.getValidations(),
    });
    this.$overlay.fadeIn();
    this.$modal.fadeIn();
    this.$emailInput.focus();
  }

  dismiss(e) {
    e && e.preventDefault && e.preventDefault();

    this.$overlay.fadeOut();
    this.$modal.fadeOut();
  }

  render() {
    const emailValid = this.state.validations.emailRequiredValid
      && this.state.validations.emailFormatValid
      && this.state.validations.emailIsUniqueValid
      && !this.state.serverError;

    return (
      <div className="email-change-modal">
        <div
          role="region"
          className="modal-overlay"
          ref={el => (this.$overlay = $(el))}
          onClick={() => this.dismiss()}
        />
        <div className="modal" tabIndex="-1" role="dialog" ref={el => (this.$modal = $(el))}>
          <article>
            <header>
              <h4>Change email address</h4>
            </header>
            <Loading ref={c => (this.loading = c)}>
              <section>
                <p className={`form-group ${emailValid ? '' : 'has-error'}`}>
                  <label htmlFor="email-change-modal-email">
                    Email:
                    <input
                      id="email-change-modal-email"
                      type="email"
                      placeholder="Email address"
                      value={this.state.email}
                      ref={el => (this.$emailInput = $(el))}
                      onChange={e => this.handleEmailChange(e)}
                    />
                    {this.state.validations.emailRequiredValid || (
                      <span className="help-block">Email address is required</span>
                    )}
                    {this.state.validations.emailFormatValid || (
                      <span className="help-block">Email address is invalid</span>
                    )}
                    {this.state.validations.emailIsUniqueValid || (
                      <span className="help-block">Email address is already in use</span>
                    )}
                    {this.state.serverError && (
                      <span className="help-block">{this.state.serverError}</span>
                    )}
                  </label>
                </p>
              </section>
              <footer>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={e => this.dismiss(e)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={e => this.save(e)}
                >
                  Change Email
                </button>
              </footer>
            </Loading>
          </article>
        </div>
      </div>
    );
  }
}
